import { apiFetch, getToken, API_BASE } from "@/shared/api/http";

export type AcademyProfile = {
  id: string;
  name: string;
  tagline: string;
  address: string;
  email: string;
  phone: string;
  dateFormat: string;
  updatedAt: string;
};

export function getAcademyProfile(): Promise<AcademyProfile> {
  return apiFetch<AcademyProfile>("/api/settings/profile");
}

export function updateAcademyProfile(data: Omit<AcademyProfile, "id" | "updatedAt">): Promise<AcademyProfile> {
  return apiFetch<AcademyProfile>("/api/settings/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export type OwnerInfo = { email: string; name: string; hasCustomPassword: boolean };

export function getOwner(): Promise<OwnerInfo> {
  return apiFetch<OwnerInfo>("/api/settings/owner");
}

export function updateOwnerName(name: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/settings/owner-name", {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export function changePassword(data: { currentPassword: string; newPassword: string }): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/settings/password", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export type BillingDefaults = {
  defaultDueDay: number;
  defaultMonthlyFee: number;
  defaultQuarterlyFee: number;
  invoicePrefix: string;
  lateFeeEnabled: boolean;
  lateFeeType: "flat" | "percent";
  lateFeeValue: number;
};

export function getBillingDefaults(): Promise<BillingDefaults> {
  return apiFetch<BillingDefaults>("/api/settings/billing-defaults");
}

export function updateBillingDefaults(data: BillingDefaults): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/settings/billing-defaults", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export type Sport = { id: string; name: string; createdAt: string };

export function getSports(): Promise<Sport[]> {
  return apiFetch<Sport[]>("/api/settings/sports");
}

export function createSport(name: string): Promise<Sport> {
  return apiFetch<Sport>("/api/settings/sports", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export function deleteSport(id: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/api/settings/sports/${id}`, { method: "DELETE" });
}

export type BatchFeePlan = {
  id: string;
  batch: string;
  threeMonthFee: number;
  sixMonthFee: number;
  twelveMonthFee: number;
  dueDay: number;
  effectiveDate: string;
  createdAt: string;
};

export function getBatchFeePlans(): Promise<BatchFeePlan[]> {
  return apiFetch<BatchFeePlan[]>("/api/settings/batch-fee-plans");
}

export function createBatchFeePlan(
  data: Omit<BatchFeePlan, "id" | "createdAt">,
): Promise<BatchFeePlan> {
  return apiFetch<BatchFeePlan>("/api/settings/batch-fee-plans", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Mirrors the Required<InvoicePdfSettings> shape returned by GET /api/settings/invoice-pdf.
// All fields are non-optional because the backend always returns defaults from AcademyProfile.
export type InvoicePdfSettings = {
  invoiceTemplate: "classic" | "modern" | "minimal";
  invoicePrimaryColor: string;
  invoiceAccentColor: string;
  invoiceFontFamily: "Helvetica" | "Times-Roman" | "Courier";
  invoiceFooterNote: string;
  invoiceShowBatch: boolean;
  invoiceShowSports: boolean;
  invoiceShowBillingPeriod: boolean;
  invoiceShowBillDate: boolean;
  invoiceShowDueDate: boolean;
  invoiceShowBillingType: boolean;
  invoiceShowDescription: boolean;
  invoiceShowQrCode: boolean;
  upiId: string;
  invoiceLogoShape: "circle" | "square" | "none";
  invoiceLogoSize: "small" | "medium" | "large";
  logoUrl?: string | null;
};

export function getInvoicePdfSettings(): Promise<InvoicePdfSettings> {
  return apiFetch<InvoicePdfSettings>("/api/settings/invoice-pdf");
}

export function updateInvoicePdfSettings(data: InvoicePdfSettings): Promise<InvoicePdfSettings> {
  return apiFetch<InvoicePdfSettings>("/api/settings/invoice-pdf", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// apiFetch forces Content-Type: application/json so FormData upload must use raw fetch
export async function uploadInvoiceLogo(file: File): Promise<{ logoUrl: string }> {
  const token = getToken() ?? "";
  const form = new FormData();
  form.append("logo", file);
  const res = await fetch(`${API_BASE}/api/settings/invoice-pdf/logo`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json() as { logoUrl: string };
  return { logoUrl: API_BASE + data.logoUrl };
}

export function deleteInvoiceLogo(): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/settings/invoice-pdf/logo", {
    method: "DELETE",
  });
}

export const DEFAULT_FEE_REMINDER_TEMPLATE =
  `Hello {{playerName}} 👋\n\nYour fee of *₹{{amount}}* for *{{period}}* at *{{academyName}}* is due on *{{dueDate}}*.\n\nKindly pay at the earliest to avoid late charges.\n\nThank you 🙏\n— {{academyName}}`;

export const DEFAULT_RECEIPT_TEMPLATE =
  `Hello {{playerName}} 👋\n\nWe've received your payment of *₹{{amount}}* for *{{period}}* at *{{academyName}}*.\n\n📅 Payment Date: *{{paidDate}}*\n💳 Payment Method: *{{paymentMethod}}*\n\nThank you for your prompt payment! 🙏\n— {{academyName}}`;

export type WhatsappTemplates = {
  waFeeReminderTemplate: string;
  waReceiptTemplate: string;
};

export function getWhatsappTemplates(): Promise<WhatsappTemplates> {
  return apiFetch<WhatsappTemplates>("/api/settings/whatsapp-templates");
}

export function updateWhatsappTemplates(data: WhatsappTemplates): Promise<WhatsappTemplates> {
  return apiFetch<WhatsappTemplates>("/api/settings/whatsapp-templates", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function downloadInvoicePreview(): Promise<void> {
  const token = getToken() ?? "";
  const res = await fetch(`${API_BASE}/api/settings/invoice-pdf/preview`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to generate preview");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "invoice-preview.pdf";
  a.click();
  URL.revokeObjectURL(url);
}
